import {
  LTLAccessorial,
  LTLAccessorialCategoryTag,
  LTLSelectedAccessorial,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Undef } from 'types/UtilityTypes';

export const CATEGORY_ORDER: LTLAccessorialCategoryTag[] = [
  'pickup',
  'delivery',
  'shipment',
];

export const CATEGORY_CONFIG: Record<
  LTLAccessorialCategoryTag,
  { label: string; color: string; badgeBg: string; badgeText: string }
> = {
  pickup: {
    label: 'Pickup',
    color: 'bg-success-400/80',
    badgeBg: 'bg-success-100/80',
    badgeText: 'text-success-700',
  },
  delivery: {
    label: 'Delivery',
    color: 'bg-accent-400/80',
    badgeBg: 'bg-accent-100/80',
    badgeText: 'text-accent-700',
  },
  shipment: {
    label: 'Shipment',
    color: 'bg-brand-400/80',
    badgeBg: 'bg-brand-100/80',
    badgeText: 'text-brand-700',
  },
};

export function getAccessorialId(accessorial: LTLAccessorial): string {
  return (
    accessorial.code ||
    (accessorial.externalId != null ? String(accessorial.externalId) : '')
  );
}

export function findAccessorialById(
  accessorials: LTLAccessorial[],
  id: string
): Undef<LTLAccessorial> {
  if (!id) {
    return undefined;
  }

  return accessorials.find(
    (a) =>
      getAccessorialId(a) === id ||
      (a.externalId != null && String(a.externalId) === id)
  );
}

export function getPrimaryCategory(
  def?: LTLAccessorial
): Undef<LTLAccessorialCategoryTag> {
  if (!def?.categoryTags?.length) {
    return undefined;
  }

  return (
    CATEGORY_ORDER.find((c) => def.categoryTags.includes(c)) ??
    def.categoryTags[0]
  );
}

export function getCountsByCategory(
  selectedAccessorials: LTLSelectedAccessorial[],
  accessorials: LTLAccessorial[]
): Record<LTLAccessorialCategoryTag, number> {
  const counts: Record<LTLAccessorialCategoryTag, number> = {
    pickup: 0,
    delivery: 0,
    shipment: 0,
  };

  selectedAccessorials.forEach((selected) => {
    const identifier =
      selected.code ||
      (selected.externalId != null ? String(selected.externalId) : '');

    if (!identifier) {
      return;
    }

    const def = findAccessorialById(accessorials, identifier);
    const category = getPrimaryCategory(def);
    if (category) {
      counts[category]++;
    }
  });

  return counts;
}

/** Sorts selected accessorials by dropdown category and catalog order. */
export function sortSelectedAccessorials(
  selectedAccessorials: LTLSelectedAccessorial[],
  accessorials: LTLAccessorial[]
): LTLSelectedAccessorial[] {
  const catalogIndex = new Map(
    accessorials.map((accessorial, index) => [
      getAccessorialId(accessorial),
      index,
    ])
  );

  return selectedAccessorials
    .map((selected, originalIndex) => ({ selected, originalIndex }))
    .sort((left, right) => {
      const leftId =
        left.selected.code || String(left.selected.externalId ?? '');
      const rightId =
        right.selected.code || String(right.selected.externalId ?? '');
      const leftCategory = getPrimaryCategory(
        findAccessorialById(accessorials, leftId)
      );
      const rightCategory = getPrimaryCategory(
        findAccessorialById(accessorials, rightId)
      );
      const leftCategoryIndex = leftCategory
        ? CATEGORY_ORDER.indexOf(leftCategory)
        : CATEGORY_ORDER.length;
      const rightCategoryIndex = rightCategory
        ? CATEGORY_ORDER.indexOf(rightCategory)
        : CATEGORY_ORDER.length;

      return (
        leftCategoryIndex - rightCategoryIndex ||
        (catalogIndex.get(leftId) ?? Number.MAX_SAFE_INTEGER) -
          (catalogIndex.get(rightId) ?? Number.MAX_SAFE_INTEGER) ||
        left.originalIndex - right.originalIndex
      );
    })
    .map(({ selected }) => selected);
}

export function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
